import { useEffect, useRef, useState } from "react";
import { ScanBarcode } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { Field } from "@/components/common";
import { FormDialog } from "@/components/kids/kidsUi";

const cls = "q-input min-h-[44px] w-full";

function useScanner(videoRef, on, onCode) {
  const [err, setErr] = useState("");
  useEffect(() => {
    if (!on) return undefined;
    let stop = () => {};
    let dead = false;
    (async () => {
      try {
        if ("BarcodeDetector" in window) {
          const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: "environment" } });
          if (dead) { stream.getTracks().forEach((t) => t.stop()); return; }
          videoRef.current.srcObject = stream;
          await videoRef.current.play();
          const det = new window.BarcodeDetector({ formats: ["ean_13", "ean_8", "upc_a", "upc_e"] });
          const id = setInterval(async () => {
            const r = await det.detect(videoRef.current).catch(() => []);
            if (r[0]?.rawValue) onCode(r[0].rawValue);
          }, 400);
          stop = () => { clearInterval(id); stream.getTracks().forEach((t) => t.stop()); };
        } else {
          const { BrowserMultiFormatReader } = await import("@zxing/browser");
          const ctl = await new BrowserMultiFormatReader().decodeFromVideoDevice(undefined, videoRef.current, (res) => res && onCode(res.getText()));
          stop = () => ctl.stop();
        }
      } catch (e) {
        setErr(e?.name === "NotAllowedError" ? "Camera permission was denied. Type the barcode number instead." : "The camera isn't available. Type the barcode number instead.");
      }
    })();
    return () => { dead = true; stop(); };
  }, [on, videoRef, onCode]);
  return err;
}

export function BarcodeDialog({ hubId, onAdd, onClose }) {
  const video = useRef(null);
  const [code, setCode] = useState("");
  const [res, setRes] = useState(null);
  const [name, setName] = useState("");
  const [camera, setCamera] = useState(true);
  const look = useRef(async (c) => {
    setCamera(false);
    setCode(c);
    try { setRes((await api.get(`/organizations/${hubId}/barcodes/${c}`)).data); } catch (e) { toast.error(errMsg(e)); }
  });
  const camErr = useScanner(video, camera, look.current);
  const addFound = () => { onAdd({ name: res.name, qty: res.size || null, note: res.brand || null }); onClose(); };
  const remember = () => api.post(`/organizations/${hubId}/barcodes`, { code: res.code, name }).then(() => { onAdd({ name }); onClose(); }).catch((e) => toast.error(errMsg(e)));
  return (
    <FormDialog open onClose={onClose} title="Scan a barcode" description="Point the camera at the barcode, or type the number." testid="barcode-dialog">
      <div className="space-y-3">
        {camera && !camErr && <video ref={video} className="aspect-video w-full rounded-xl bg-black object-cover" muted playsInline data-testid="barcode-video" />}
        {camErr && <p className="text-sm font-semibold text-amber-300" data-testid="barcode-camera-error">{camErr}</p>}
        <form className="flex gap-2" onSubmit={(e) => { e.preventDefault(); look.current(code.replace(/\D/g, "")); }}>
          <input className={cls} inputMode="numeric" placeholder="Barcode number" value={code} onChange={(e) => setCode(e.target.value)} data-testid="barcode-input" />
          <button className="q-btn q-btn-primary min-h-[44px] shrink-0" disabled={code.replace(/\D/g, "").length < 6} data-testid="barcode-lookup">Look up</button>
        </form>
        {res?.found && <div className="q-card space-y-2 p-3" data-testid="barcode-result">
          <b className="block text-q-text" data-testid="barcode-name">{res.name}</b>
          <span className="block text-xs text-q-muted" data-testid="barcode-brand">{[res.brand, res.size].filter(Boolean).join(" · ") || "—"}</span>
          <button className="q-btn q-btn-primary min-h-[44px] w-full" onClick={addFound} data-testid="barcode-add">Add to list</button></div>}
        {res && !res.found && <div className="space-y-2" data-testid="barcode-unknown">
          <p className="text-sm text-q-muted">{res.message}</p>
          <Field label="Product name"><input className={cls} value={name} onChange={(e) => setName(e.target.value)} data-testid="barcode-name-input" /></Field>
          <button className="q-btn q-btn-primary min-h-[44px] w-full" disabled={!name.trim()} onClick={remember} data-testid="barcode-remember">Add and remember</button></div>}
        {!camera && !camErr && <button className="q-btn q-btn-ghost min-h-[44px] w-full" onClick={() => { setRes(null); setCamera(true); }} data-testid="barcode-rescan"><ScanBarcode className="h-4 w-4" /> Scan another</button>}
      </div>
    </FormDialog>
  );
}
